/* Copyright (C) 2023 Advenio Tecnosys Pvt. Ltd.

*        All Rights Reserved

*

 * NOTICE: 

 * All information contained herein is, and remains

* the property of Advenio Tecnosys and its suppliers,

* if any.  The intellectual and technical concepts contained

* herein are proprietary to Advenio Tecnosys

* and its suppliers and may be covered by Indian, U.S. and Foreign Patents,

* patents in process, and are protected by trade secret or copyright law.

* Appropriation, redistribution, dissemination of this information or reproduction

* of this material is strictly forbidden unless prior written permission is obtained

* from Advenio Tecnosys Pvt. Ltd.

*

* Author: < Mylavarapu Sarath Sai, Amal Sabu, Ujjwal Singh >

*/

import { getAuthTokenFromSessionStorage, stripInlineBase64FromWire } from "./common";
import {
  reconcileReservationS3Upload,
  reservationS3Upload,
} from "./reservationS3Upload";
import axios from "axios";
import { baseURL, syncBaseUrl } from "database/onlineDb/baseUrl";
import {
  updatePatientID,
  deletePatient,
  updatePatient,
  getPatientDetailById,
  getViewPatients,
  updateViewPatient,
  //  getAllViewPatients,
} from "database/offlineDb/indexDb";
import { lookupTable } from "data/data";
// import { getPatientsList } from "actions/patient-action";
import { saveAs } from "file-saver";
import moment from "moment";
import { v4 as uuid } from "uuid";

import { timezone } from "actions";
import {
  getViewPatientPersistenceVersion,
  serializeViewPatientPersistence,
} from "../realtime/viewPatientPersistenceQueue";
import { isMlEntitlementEnabled } from "../realtime/entitlementMode";
import { createEntitlementReservationClient } from "../realtime/entitlementReservationClient";
import {
  createBulkReservationAdmission,
  reconcilePostConfirmFailure,
} from "../realtime/bulkReservationAdmission";
import {
  applyPatientReservationPermissions,
  createPatientReservationPlan,
} from "../realtime/patientReservationPlan";
import {
  patientSyncFlightKey,
  runPatientSyncFlight,
} from "../realtime/patientSyncFlight";
import { patientUploadResyncTargetKey } from "../realtime/patientUploadResyncTarget";
import {
  isNotReadyReportError,
  patientHasServerImages,
} from "../realtime/patientReportFetchGate";

var speedData = navigator.connection;

const isActivePatientRealtimeOwner = (patient, props) =>
  patient?.mrn !== undefined &&
  patient?.mrn !== null &&
  props?.patientStore?.activeRealtimePatientMrn !== undefined &&
  props?.patientStore?.activeRealtimePatientMrn !== null &&
  String(patient.mrn) === String(props.patientStore.activeRealtimePatientMrn);

const reportReportStatusFailure = (error) => {
  if (isNotReadyReportError(error)) return;
  console.warn("report status fetch failed", error);
};

const confirmedPatientWrite = (result) =>
  result === true || (typeof result === "number" && result > 0);

const requirePatientWrite = (result) => {
  if (!confirmedPatientWrite(result)) {
    throw new Error("We couldn't save your changes on this device. Please try again.");
  }
  return result;
};

const stalePatientSyncError = () => {
  const error = new Error("patient sync scope is stale");
  error.code = "PATIENT_SYNC_STALE";
  return error;
};

const metadataUncertainError = (originalError) => {
  const conflict = Number(originalError?.response?.status) === 409;
  const error = new Error(
    conflict
      ? "image metadata conflicts with the authorized upload"
      : "image metadata outcome is uncertain"
  );
  error.code = conflict
    ? "PATIENT_SYNC_METADATA_CONFLICT"
    : "PATIENT_SYNC_METADATA_UNCERTAIN";
  error.originalError = originalError;
  return error;
};

const requireCurrentPatientSync = (scope = {}) => {
  if (typeof scope.isCurrent === "function" && !scope.isCurrent()) {
    throw stalePatientSyncError();
  }
};

const persistLatestViewPatient = (patient, patch, scope) =>
  serializeViewPatientPersistence(async () => {
    requireCurrentPatientSync(scope);
    const current = await getViewPatients(patient.mrn);
    requireCurrentPatientSync(scope);
    const nextPatient = { ...patient, ...(current?.data || {}), ...patch };
    requirePatientWrite(await updateViewPatient(nextPatient));
    requireCurrentPatientSync(scope);
    return nextPatient;
  });

const reportSnapshotPatch = (patient) => ({
  isReportAvailable: patient.isReportAvailable,
  imgDataOnServer: patient.imgDataOnServer,
  imgOnServer: patient.imgOnServer,
  left: patient.left,
  right: patient.right,
  backUpReport: patient.backUpReport,
  lastRequestedOn: patient.lastRequestedOn,
  restDetailOnServer: patient.restDetailOnServer,
});

const pendingPatientUploadImages = (patient) => {
  const details = patient?.retinalImagesData?.retinalImagesDetails || {};
  return [
    ...(Array.isArray(details.imageLeftEye) ? details.imageLeftEye : []),
    ...(Array.isArray(details.imageRightEye) ? details.imageRightEye : []),
  ].filter((image) => !image?.fileData?.uploaded);
};

export const patientHasAttachedImages = (patient) => {
  const details = patient?.retinalImagesData?.retinalImagesDetails;
  const left = Array.isArray(details?.imageLeftEye) ? details.imageLeftEye : [];
  const right = Array.isArray(details?.imageRightEye) ? details.imageRightEye : [];
  return left.length > 0 || right.length > 0;
};

const patientUploadReservationPlan = (patient, props) => {
  const noAiAnnotation = props?.globalStore?.orgzFeatures?.no_ai_annotation;
  if (!isMlEntitlementEnabled(noAiAnnotation)) {
    throw new Error("Organization upload mode is unavailable");
  }
  const userDetail = props?.userStore?.userProfile || {};
  const licensedModels = Array.isArray(userDetail?.license?.algos)
    ? userDetail.license.algos
    : props?.userStore?.userSettings?.algorithms;
  return createPatientReservationPlan({
    images: pendingPatientUploadImages(patient),
    models: licensedModels,
    s3Bucket: `${process.env.REACT_APP_BUCKET_TYPE}-${userDetail.orgz_id}`,
  });
};

const canonicalPatientUploadUnits = (plan) =>
  plan.apiRequest.units.map(({ canonicalUnitIdentity }) => canonicalUnitIdentity);

const matchingPatientUploadAttempt = (attempt, canonicalUnitIdentities) =>
  Boolean(
    attempt &&
      Array.isArray(attempt.canonicalUnitIdentities) &&
      JSON.stringify(attempt.canonicalUnitIdentities) ===
        JSON.stringify(canonicalUnitIdentities) &&
      attempt.quote &&
      attempt.idempotencyKey
  );

const patientUploadQuoteIsCurrent = (quote) => {
  const expiresAt = Date.parse(quote?.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
};

const PATIENT_RESERVATION_ID = /^[0-9a-f]{32}$/;

const hasDurablePatientUploadPermissions = (images) =>
  images.every(
    (image) =>
      image?.reservationPermission &&
      image?.fileData?.reservationPermissionId ===
        image.reservationPermission.permissionId &&
      image?.fileData?.reservationBatchId ===
        image.reservationPermission.reservationId
  );

const authorizedPatientUploadCanResume = ({ attempt, plan, images }) => {
  if (
    attempt?.state !== "AUTHORIZED" ||
    !Array.isArray(attempt.canonicalUnitIdentities) ||
    !attempt.reservationId ||
    !Array.isArray(images) ||
    images.length !== plan?.units?.length
  ) {
    return false;
  }
  const originalIdentities = attempt.canonicalUnitIdentities;
  const pendingIdentities = canonicalPatientUploadUnits(plan);
  if (
    new Set(originalIdentities).size !== originalIdentities.length ||
    new Set(pendingIdentities).size !== pendingIdentities.length ||
    pendingIdentities.some((identity) => !originalIdentities.includes(identity))
  ) {
    return false;
  }
  return images.every((image, index) => {
    const planUnit = plan.units[index];
    const permission = image?.reservationPermission;
    const fileData = image?.fileData;
    const originalUnitIndex = originalIdentities.indexOf(
      pendingIdentities[index]
    );
    const members = Array.isArray(planUnit?.members) ? planUnit.members : [];
    const memberSetSequences = [
      ...new Set(members.map((member) => member.memberSetSequence)),
    ];
    const models = members
      .map((member) => member.model)
      .filter((model) => typeof model === "string");
    const validMemberPlan =
      members.length > 0 &&
      memberSetSequences.length === 1 &&
      Number.isSafeInteger(memberSetSequences[0]) &&
      memberSetSequences[0] > 0 &&
      members.every((member, memberIndex) =>
        typeof member.model === "string"
          ? member.workSequence === memberIndex + 1
          : members.length === 1 && member.workSequence === undefined
      );
    return Boolean(
      permission &&
        fileData &&
        originalUnitIndex >= 0 &&
        validMemberPlan &&
        PATIENT_RESERVATION_ID.test(attempt.reservationId) &&
        PATIENT_RESERVATION_ID.test(permission.permissionId || "") &&
        PATIENT_RESERVATION_ID.test(permission.reservationId || "") &&
        permission.unitSequence === originalUnitIndex + 1 &&
        permission.memberSetSequence === memberSetSequences[0] &&
        fileData.guid === planUnit.guid &&
        fileData.laterality === planUnit.laterality &&
        permission.guid === planUnit.guid &&
        permission.laterality === planUnit.laterality &&
        permission.s3Bucket === planUnit.s3Bucket &&
        permission.s3Key === planUnit.s3Key &&
        permission.reservationId === attempt.reservationId &&
        fileData.reservationPermissionId === permission.permissionId &&
        fileData.reservationBatchId === permission.reservationId &&
        fileData.reservationUnitSequence === permission.unitSequence &&
        fileData.reservationMemberSetSequence === permission.memberSetSequence &&
        fileData.reservationWorkflow === plan.workflow &&
        fileData.reservationExpectedMemberSetCount ===
          memberSetSequences.length &&
        JSON.stringify(fileData.reservationModels) === JSON.stringify(models) &&
        fileData.s3Bucket === permission.s3Bucket &&
        fileData.s3Key === permission.s3Key
    );
  });
};

const persistPatientReservationAttempt = async (patient, scope = {}) => {
  const persist = scope.persistPatient || updatePatient;
  requirePatientWrite(await persist(patient));
  requireCurrentPatientSync(scope);
};

export const preparePatientUploadReservation = async (
  patient,
  props,
  scope = {}
) => {
  requireCurrentPatientSync(scope);
  const images = pendingPatientUploadImages(patient);
  if (images.length === 0) return { outcome: "ignored" };
  const plan = patientUploadReservationPlan(patient, props);
  const canonicalUnitIdentities = canonicalPatientUploadUnits(plan);
  const previousAttempt = patient.patientUploadReservationAttempt;
  const sameAttempt = matchingPatientUploadAttempt(
    previousAttempt,
    canonicalUnitIdentities
  );
  if (previousAttempt?.state === "AUTHORIZED") {
    if (
      !authorizedPatientUploadCanResume({
        attempt: previousAttempt,
        plan,
        images,
      })
    ) {
      throw new Error("We couldn't continue saving the eye images. Please try saving again.");
    }
    return {
      outcome: "upload-retry-required",
      quote: previousAttempt.quote,
      idempotencyKey: previousAttempt.idempotencyKey,
    };
  }
  if (
    !scope.forceRequote &&
    sameAttempt &&
    ["QUOTED", "CONFIRMING"].includes(previousAttempt.state) &&
    patientUploadQuoteIsCurrent(previousAttempt.quote)
  ) {
    return {
      outcome: "confirmation-required",
      quote: previousAttempt.quote,
      idempotencyKey: previousAttempt.idempotencyKey,
    };
  }
  const reservationClient = createEntitlementReservationClient({
    token: scope.authToken || getAuthTokenFromSessionStorage(),
    baseUrl: baseURL,
  });
  const admission = createBulkReservationAdmission({ reservationClient });
  const quote = await admission.quote(plan);
  patient.patientUploadReservationAttempt = {
    state: "QUOTED",
    canonicalUnitIdentities,
    quote,
    idempotencyKey: `confirm-${uuid()}`,
  };
  await persistPatientReservationAttempt(patient, scope);
  return {
    outcome: "confirmation-required",
    quote,
    idempotencyKey: patient.patientUploadReservationAttempt.idempotencyKey,
  };
};

export const beginPatientUploadReservationConfirmation = async (
  patient,
  props,
  scope = {}
) => {
  requireCurrentPatientSync(scope);
  const plan = patientUploadReservationPlan(patient, props);
  const canonicalUnitIdentities = canonicalPatientUploadUnits(plan);
  const attempt = patient.patientUploadReservationAttempt;
  if (!matchingPatientUploadAttempt(attempt, canonicalUnitIdentities)) {
    throw new Error("The cost estimate for saving these images is out of date. Please try saving again.");
  }
  if (!patientUploadQuoteIsCurrent(attempt.quote)) {
    const reservationClient = createEntitlementReservationClient({
      token: scope.authToken || getAuthTokenFromSessionStorage(),
      baseUrl: baseURL,
    });
    const admission = createBulkReservationAdmission({ reservationClient });
    const quote = await admission.quote(plan);
    patient.patientUploadReservationAttempt = {
      ...attempt,
      state: "QUOTED",
      quote,
      idempotencyKey: `confirm-${uuid()}`,
    };
    await persistPatientReservationAttempt(patient, scope);
  }
  if (attempt.state === "AUTHORIZED") {
    throw new Error("These images are already confirmed. Please refresh the page.");
  }
  if (attempt.state !== "QUOTED" && attempt.state !== "CONFIRMING") {
    throw new Error("We couldn't confirm the cost estimate for saving these images. Please try saving again.");
  }
  if (attempt.state !== "CONFIRMING") {
    patient.patientUploadReservationAttempt = {
      ...patient.patientUploadReservationAttempt,
      state: "CONFIRMING",
    };
    await persistPatientReservationAttempt(patient, scope);
  }
  return patient.patientUploadReservationAttempt;
};

const configuredAlgorithmValues = (algorithms) =>
  (Array.isArray(algorithms)
    ? algorithms
    : typeof algorithms === "string"
    ? algorithms.split(",")
    : []
  )
    .map((algorithm) =>
      typeof algorithm === "string" ? algorithm.trim() : ""
    )
    .filter(Boolean);

const viewPatientEyeFromSnapshot = (user, laterality, images) => {
  if (!Array.isArray(images) || images.length === 0) return [];
  const priorities = images.map((image) => getPriority(image.status, lookupTable));
  const winner = images[priorities.indexOf(Math.max(...priorities))];
  if (!winner) return [];
  const existing = Array.isArray(user?.[laterality])
    ? user[laterality].find((image) => image?.data?.guid === winner.guid) ||
      user[laterality][0]
    : null;
  const storedImages =
    laterality === "left"
      ? user?.retinalImagesData?.retinalImagesDetails?.imageLeftEye
      : user?.retinalImagesData?.retinalImagesDetails?.imageRightEye;
  const stored = Array.isArray(storedImages)
    ? storedImages.find(
        (image) =>
          (image?.fileData?.guid || image?.data?.guid || image?.guid) ===
          winner.guid
      )
    : null;
  const url =
    existing?.url ||
    stored?.imgUrl ||
    stored?.url ||
    stored?.imageUrl ||
    stored?.fileData?.imageUrl;
  return [
    {
      ...(existing || {}),
      ...(url ? { url } : {}),
      data: { ...(existing?.data || {}), ...winner },
    },
  ];
};

export const getReportStatus = (
  user,
  restDetailOnServer = false,
  props,
  viewPatientOrigin,
  scope = {}
) => {
  const isCurrent = () => {
    try {
      return typeof scope.isCurrent !== "function" || scope.isCurrent();
    } catch (error) {
      return false;
    }
  };
  if (!isCurrent()) return Promise.resolve({ outcome: "ignored" });
  if (
    !user.basicDetailOnServer &&
    !user.imgOnServer &&
    !user.imgDataOnServer &&
    !user.restDetailOnServer
  ) {
    return "";
  } else {
    let para = JSON.stringify(props.userStore?.userSettings.algorithms);

    if (navigator.onLine) {
      return axios({
        method: "get",
        url: syncBaseUrl + "/report/patient/" + user.mrn + `/${para}`,
        data: {
          algorithms: props.userStore?.userSettings.algorithms,
        },
        headers: {
          Accept: "*/*",
          authorization: scope.authToken || getAuthTokenFromSessionStorage(),
          timezone,
        },
      }).then((res) => {
        if (!isCurrent()) return { outcome: "ignored" };
        const leftImages =
          res.data !== undefined
            ? res.data.images
                .filter((item) => item.laterality === "left")
                .map((obj) => {
                  return { ...obj, dr_status: null };
                })
            : [];
        const rightImages =
          res.data !== undefined
            ? res.data.images
                .filter((item) => item.laterality === "right")
                .map((obj) => {
                  return { ...obj, dr_status: null };
                })
            : [];

        let persistence = Promise.resolve();
        if (props.userStore?.userSettings.algorithms.length !== 0) {
          const statusArrLeft = [];
          leftImages.map((item, index) => {
            statusArrLeft.push(getPriority(item.status, lookupTable));
          });
          const maxPriorityLeft = Math.max(...statusArrLeft);

          const left = viewPatientEyeFromSnapshot(user, "left", leftImages);

          const statusArrRight = [];
          rightImages.map((item, index) => {
            statusArrRight.push(getPriority(item.status, lookupTable));
          });
          const maxPriorityRight = Math.max(...statusArrRight);
          const right = viewPatientEyeFromSnapshot(user, "right", rightImages);

          const isReportAvailable =
            Number(maxPriorityLeft) > 10 || Number(maxPriorityRight) > 10;
          const nextPatient = viewPatientOrigin
            ? {
                ...user,
                isReportAvailable,
                imgDataOnServer: true,
                imgOnServer: true,
                left,
                right,
                backUpReport: { images: res.data.images },
                lastRequestedOn: new Date().getTime() / 1000,
                restDetailOnServer,
              }
            : {
                ...user,
                isReportAvailable,
                backUpReport: { images: res.data.images },
                leftEyeStatus: getStatus(maxPriorityLeft, lookupTable),
                rightEyeStatus: getStatus(maxPriorityRight, lookupTable),
                lastRequestedOn: new Date().getTime() / 1000,
                restDetailOnServer,
              };
          const write = viewPatientOrigin
            ? props.writeViewPatient || updateViewPatient
            : props.writePatient || updatePatient;
          persistence = Promise.resolve(write(nextPatient)).then((result) => {
            if (result?.outcome === "ignored") return result;
            requirePatientWrite(result);
            if (!isCurrent()) return { outcome: "ignored" };
            if (
              viewPatientOrigin &&
              isReportAvailable &&
              props.getAllViewPatientsFromIDB
            ) {
              return props.getAllViewPatientsFromIDB();
            }
            return props.getPatientsList();
          });
        }
        return persistence.then(() => {
          if (!isCurrent()) return { outcome: "ignored" };
          if (
            res.data.images?.filter((image) => image.status === "Pending")
              ?.length === 0
          ) {
            props?.updateOrgCountsAndBalances &&
              props.updateOrgCountsAndBalances();
          }
          return res.data;
        });
      });
    } else {
      return getPatientDetailById(Number(user.mrn)).then((res) => {
        if (!isCurrent()) return { outcome: "ignored" };
        const filteredByAlgo = [];
        configuredAlgorithmValues(
          props.userStore?.userSettings.algorithms
        ).forEach((item) => {
          filteredByAlgo.push(
            ...getStatusByAlgo(
              item,
              res.data.backUpReport.images !== undefined
                ? res.data.backUpReport.images
                : []
            )
          );
        });
        let leftImages = filteredByAlgo.filter(
          (item) => item.laterality === "left"
        );
        let rightImages = filteredByAlgo.filter(
          (item) => item.laterality === "right"
        );

        let statusArrLeft = [];
        leftImages.map((item) => {
          statusArrLeft.push(getPriority(item.status, lookupTable));
        });
        let maxPriorityLeft = Math.max(...statusArrLeft);
        let statusArrRight = [];
        rightImages.map((item) => {
          statusArrRight.push(getPriority(item.status, lookupTable));
        });
        let maxPriorityRight = Math.max(...statusArrRight);

        const isReportAvailable =
          Number(maxPriorityLeft) > 10 || Number(maxPriorityRight) > 10;
        const nextPatient = viewPatientOrigin
          ? {
              ...user,
              isReportAvailable,
              left: viewPatientEyeFromSnapshot(user, "left", leftImages),
              right: viewPatientEyeFromSnapshot(user, "right", rightImages),
              backUpReport: res.data.backUpReport,
              restDetailOnServer,
            }
          : {
              ...user,
              isReportAvailable,
              leftEyeStatus: getStatus(maxPriorityLeft, lookupTable),
              rightEyeStatus: getStatus(maxPriorityRight, lookupTable),
              restDetailOnServer,
            };
        const write = viewPatientOrigin
          ? props.writeViewPatient || updateViewPatient
          : props.writePatient || updatePatient;
        return Promise.resolve(write(nextPatient)).then((result) => {
          requirePatientWrite(result);
          if (!isCurrent()) return { outcome: "ignored" };
          return viewPatientOrigin
            ? props.getAllViewPatientsFromIDB()
            : props.getPatientsList();
        });
      });
    }
  }
};

export const getStatusByAlgo = (algo, status) => {
  let value = [];
  for (let i = 0; i < status.length; i++) {
    if (status[i].Model === algo) {
      value.push(status[i]);
    }
  }
  return value;
};

export const getStatus = (priority, array) => {
  let value = "";
  for (let i = 0; i < array.length; i++) {
    if (array[i].priority === priority) {
      value = array[i].status;
    }
  }

  return value;
};
const getPriority = (label, array) => {
  let value = "";
  for (let i = 0; i < array.length; i++) {
    if (array[i].status === label) {
      value = array[i].priority;
    }
  }
  return value;
};
async function imagaAndRestDataSyncOperation(
  patient,
  props,
  // originDetails,
  imgAndRestDataSyncHelperDetailsForVP,
  setimgAndRestDataSyncHelperDetailsForVP,
  viewPatientOrigin = false,
  isLastViewPatient = false,
  scope = {}
) {
  const noAiAnnotation = props?.globalStore?.orgzFeatures?.no_ai_annotation;
  const userDetail = props.userStore.userProfile;
  const imageData = patient.retinalImagesData?.retinalImagesDetails || {};
  let leftImageData = Array.isArray(imageData.imageLeftEye)
    ? imageData.imageLeftEye
    : [];
  let rightImageData = Array.isArray(imageData.imageRightEye)
    ? imageData.imageRightEye
    : [];
  const allImagesUploaded = (images) =>
    images.every((image) => image?.fileData?.uploaded === true);
  const shouldUpload = (image) =>
    !image?.fileData?.uploaded &&
    (!viewPatientOrigin || image?.imgUrl?.includes("blob"));
  let pendingImages = [...leftImageData, ...rightImageData].filter(shouldUpload);
  if (pendingImages.length === 0) return { outcome: "ignored" };

  const persistPatientUploadState = async () => {
    if (viewPatientOrigin) {
      const persisted = await persistLatestViewPatient(
        patient,
        {
          retinalImagesData: patient.retinalImagesData,
          patientUploadReservationAttempt:
            patient.patientUploadReservationAttempt,
        },
        scope
      );
      Object.assign(patient, persisted);
      return;
    }
    requirePatientWrite(await updatePatient(patient));
    requireCurrentPatientSync(scope);
  };

  if (!isMlEntitlementEnabled(noAiAnnotation)) {
    throw new Error("Organization upload mode is unavailable");
  }

  {
    const licensedModels = Array.isArray(userDetail?.license?.algos)
      ? userDetail.license.algos
      : props?.userStore?.userSettings?.algorithms;
    const plan = createPatientReservationPlan({
      images: pendingImages,
      models: licensedModels,
      s3Bucket: `${process.env.REACT_APP_BUCKET_TYPE}-${userDetail.orgz_id}`,
    });
    const previousAttempt = patient.patientUploadReservationAttempt;
    const isAuthorizedAttempt = previousAttempt?.state === "AUTHORIZED";
    const authorizedCanResume = authorizedPatientUploadCanResume({
      attempt: previousAttempt,
      plan,
      images: pendingImages,
    });
    if (isAuthorizedAttempt && !authorizedCanResume) {
      throw new Error("We couldn't continue saving the eye images. Please try saving again.");
    }
    const hasDurablePermissions = isAuthorizedAttempt
      ? authorizedCanResume
      : hasDurablePatientUploadPermissions(pendingImages);
    if (!hasDurablePermissions) {
      const reservationClient = createEntitlementReservationClient({
        token: scope.authToken || getAuthTokenFromSessionStorage(),
        baseUrl: baseURL,
      });
      const admission = createBulkReservationAdmission({ reservationClient });
      const canonicalUnitIdentities = plan.apiRequest.units.map(
        ({ canonicalUnitIdentity }) => canonicalUnitIdentity
      );
      const canReplayAttempt =
        matchingPatientUploadAttempt(
          previousAttempt,
          canonicalUnitIdentities
        ) &&
        ["QUOTED", "CONFIRMING"].includes(previousAttempt.state) &&
        patientUploadQuoteIsCurrent(previousAttempt.quote);
      if (!canReplayAttempt) {
        if (previousAttempt?.reservationId) {
          try {
            await reservationClient.cancelReservation({
              reservationId: previousAttempt.reservationId,
            });
          } catch (error) {}
        }
        const quote = await admission.quote(plan);
        patient.patientUploadReservationAttempt = {
          state: "QUOTED",
          canonicalUnitIdentities,
          quote,
          idempotencyKey: `confirm-${uuid()}`,
        };
        await persistPatientUploadState();
        return {
          outcome: "confirmation-required",
          quote,
          idempotencyKey: patient.patientUploadReservationAttempt.idempotencyKey,
        };
      }
      if (previousAttempt.state !== "CONFIRMING") {
        return {
          outcome: "confirmation-required",
          quote: previousAttempt.quote,
          idempotencyKey: previousAttempt.idempotencyKey,
        };
      }
      const admitted = await admission.confirm({
        plan,
        quote: previousAttempt.quote,
        idempotencyKey: previousAttempt.idempotencyKey,
      });
      const admittedImages = applyPatientReservationPermissions({
        images: pendingImages,
        admission: admitted,
      });
      const admittedByIdentity = new Map(
        admittedImages.map((image) => [
          `${image.fileData.guid}:${image.fileData.laterality}`,
          image,
        ])
      );
      const applyAdmission = (image) =>
        admittedByIdentity.get(
          `${image.fileData.guid}:${image.fileData.laterality}`
        ) || image;
      leftImageData = leftImageData.map(applyAdmission);
      rightImageData = rightImageData.map(applyAdmission);
      patient.retinalImagesData = {
        ...patient.retinalImagesData,
        retinalImagesDetails: {
          ...imageData,
          imageLeftEye: leftImageData,
          imageRightEye: rightImageData,
        },
      };
      patient.patientUploadReservationAttempt = {
        ...patient.patientUploadReservationAttempt,
        state: "AUTHORIZED",
        reservationId: admitted.reservation.reservationId,
      };
      pendingImages = [...leftImageData, ...rightImageData].filter(shouldUpload);
      try {
        await persistPatientUploadState();
      } catch (error) {
        throw await reconcilePostConfirmFailure({
          reservationClient,
          reservationId: admitted.reservation.reservationId,
          error,
        });
      }
    }
  }

  const uploadImage = async (image) => {
    requireCurrentPatientSync(scope);
    {
      const token = scope.authToken || getAuthTokenFromSessionStorage();
      if (image.fileData.uploadErrorCode === "METADATA_RECONCILIATION_REQUIRED") {
        // S3 completion is already durable; retry only the idempotent metadata attach.
      } else if (
        ["UPLOAD_ATTEMPT_STARTED", "UPLOAD_OUTCOME_UNKNOWN"].includes(
          image.fileData.uploadErrorCode
        )
      ) {
        try {
          await reconcileReservationS3Upload({
            permission: image?.reservationPermission,
            token,
          });
        } catch (error) {
          if (
            !["UPLOAD_OBJECT_ABSENT", "UPLOAD_OUTCOME_UNKNOWN"].includes(
              error?.code
            ) ||
            !image.file
          ) {
            throw error;
          }
          await reservationS3Upload({
            file: image.file,
            permission: image?.reservationPermission,
            token,
          });
        }
        image.fileData.uploadErrorCode = "METADATA_RECONCILIATION_REQUIRED";
        await persistPatientUploadState();
      } else {
        image.fileData.uploadErrorCode = "UPLOAD_ATTEMPT_STARTED";
        await persistPatientUploadState();
        try {
          await reservationS3Upload({
            file: image?.file,
            permission: image?.reservationPermission,
            token,
          });
        } catch (error) {
          if (error?.code === "UPLOAD_OUTCOME_UNKNOWN") {
            image.fileData.uploadErrorCode = "UPLOAD_OUTCOME_UNKNOWN";
            try {
              await persistPatientUploadState();
            } catch {}
          }
          throw error;
        }
        image.fileData.uploadErrorCode = "METADATA_RECONCILIATION_REQUIRED";
        await persistPatientUploadState();
      }
    }
    requireCurrentPatientSync(scope);
    try {
      const metadataImage = { ...image.fileData };
      delete metadataImage.uploadErrorCode;
      await axios({
        method: "post",
        url: syncBaseUrl + `/img`,
        data: {
          images: [{ ...metadataImage, uploaded: true }],
          orgz_id: userDetail.orgz_id,
          mrn: patient.mrn,
          devc_id: -1,
          loc_id: -1,
        },
        headers: {
          Accept: "*/*",
          authorization: scope.authToken || getAuthTokenFromSessionStorage(),
          "Access-Control-Allow-Origin": "*",
        },
      });
    } catch (error) {
      throw metadataUncertainError(error);
    }
    try {
      requireCurrentPatientSync(scope);
    } catch (error) {
      throw metadataUncertainError(error);
    }
    image.fileData.uploaded = true;
    delete image.fileData.uploadErrorCode;
  };
  const uploadOutcomes = await (async () => {
    const outcomes = [];
    for (const image of pendingImages) {
      try {
        await uploadImage(image);
        outcomes.push({ status: "fulfilled" });
      } catch (reason) {
        outcomes.push({ status: "rejected", reason });
        break;
      }
    }
    return outcomes;
  })();
  const failedUpload = uploadOutcomes.find(
    (outcome) => outcome.status === "rejected"
  );
  if (failedUpload) throw failedUpload.reason;
  requireCurrentPatientSync(scope);
  const uploadedAllLeftImages = allImagesUploaded(leftImageData);
  const uploadedAllRightImages = allImagesUploaded(rightImageData);
  const uploadedPatch = {
    retinalImagesData: patient.retinalImagesData,
    imgOnServer: uploadedAllLeftImages && uploadedAllRightImages,
    imgDataOnServer: uploadedAllLeftImages && uploadedAllRightImages,
  };

  let updatedPatient;
  if (viewPatientOrigin) {
    if (uploadedAllLeftImages && leftImageData.length > 0) {
      uploadedPatch.imgOnServerLeft = true;
    }
    updatedPatient = await persistLatestViewPatient(patient, uploadedPatch, scope);
    if (
      isLastViewPatient &&
      typeof setimgAndRestDataSyncHelperDetailsForVP === "function"
    ) {
      setimgAndRestDataSyncHelperDetailsForVP((prevState) => ({
        ...prevState,
        imgDataSyncDone: true,
      }));
    }
  } else {
    updatedPatient = { ...patient, ...uploadedPatch };
    requirePatientWrite(await updatePatient(updatedPatient));
    requireCurrentPatientSync(scope);
  }

  if (typeof scope.onMetadataDurable === "function") {
    scope.onMetadataDurable(updatedPatient);
  }

  await restDataSync(
    updatedPatient,
    props,
    viewPatientOrigin,
    setimgAndRestDataSyncHelperDetailsForVP,
    scope
  );
  return { outcome: "applied", patient: updatedPatient };
}

export function imagaAndRestDataSync(
  patient,
  props,
  imgAndRestDataSyncHelperDetailsForVP,
  setimgAndRestDataSyncHelperDetailsForVP,
  viewPatientOrigin = false,
  isLastViewPatient = false,
  scope = {}
) {
  const user = props?.userStore?.userProfile || {};
  const authToken = scope.authToken || getAuthTokenFromSessionStorage();
  const key = patientSyncFlightKey({
    orgId: user.orgz_id,
    userId: user.user_id,
    authToken,
    mrn: patient?.mrn,
  });
  return runPatientSyncFlight(key, () =>
    imagaAndRestDataSyncOperation(
      patient,
      props,
      imgAndRestDataSyncHelperDetailsForVP,
      setimgAndRestDataSyncHelperDetailsForVP,
      viewPatientOrigin,
      isLastViewPatient,
      { ...scope, authToken }
    )
  );
}
export async function restDataSync(
  patient,
  props,
  viewPatientOrigin,
  setimgAndRestDataSyncHelperDetailsForVP,
  scope = {}
) {
  requireCurrentPatientSync(scope);
  const latestPatient = viewPatientOrigin
    ? (await getViewPatients(patient.mrn))?.data || patient
    : patient;
  requireCurrentPatientSync(scope);
  let patientRestData = stripInlineBase64FromWire({
    personalData: latestPatient.personalData,
    patientHistoryData: latestPatient.patientHistoryData,
    lifeStyleData: latestPatient.lifeStyleData,
    clinicalConditionData: latestPatient.clinicalConditionData,
  });
  await axios({
    method: "put",
    url: syncBaseUrl + "/patient-reg/" + patient.mrn,
    data: {
      mrn: patient.mrn,
      ...patientRestData,
      ...props.globalStore.userAgentData.ua__summary,
    },
    headers: {
      Accept: "*/*",
      authorization: scope.authToken || getAuthTokenFromSessionStorage(),
      "Access-Control-Allow-Origin": "*",
    },
  });
  requireCurrentPatientSync(scope);
  let updatedPatientWithImgDataAndRestData;
  if (viewPatientOrigin) {
    updatedPatientWithImgDataAndRestData = await persistLatestViewPatient(
      latestPatient,
      { restDetailOnServer: true },
      scope
    );
    if (typeof setimgAndRestDataSyncHelperDetailsForVP === "function") {
      setimgAndRestDataSyncHelperDetailsForVP((prevState) => ({
        ...prevState,
        imgDataSyncDone: true,
        restDataSyncDone: true,
      }));
    }
    if (!isActivePatientRealtimeOwner(updatedPatientWithImgDataAndRestData, props)) {
      const reportVersion = getViewPatientPersistenceVersion();
      const reportScope = {
        ...scope,
        isCurrent: () => {
          try {
            requireCurrentPatientSync(scope);
            return getViewPatientPersistenceVersion() === reportVersion;
          } catch (error) {
            return false;
          }
        },
      };
      const reportProps = {
        ...props,
        writeViewPatient: (nextPatient) =>
          persistLatestViewPatient(
            nextPatient,
            reportSnapshotPatch(nextPatient),
            reportScope
          ).then((result) =>
            result?.outcome === "ignored" ? result : 1
          ),
      };
      await getReportStatus(
        updatedPatientWithImgDataAndRestData,
        true,
        reportProps,
        viewPatientOrigin,
        reportScope
      );
    }
    requireCurrentPatientSync(scope);
    await props.getAllViewPatientsFromIDB();
  } else {
    updatedPatientWithImgDataAndRestData = {
      ...latestPatient,
      restDetailOnServer: true,
    };
    requirePatientWrite(await updatePatient(updatedPatientWithImgDataAndRestData));
    requireCurrentPatientSync(scope);
    if (
      !isActivePatientRealtimeOwner(updatedPatientWithImgDataAndRestData, props) &&
      patientHasServerImages(updatedPatientWithImgDataAndRestData)
    ) {
      await getReportStatus(
        updatedPatientWithImgDataAndRestData,
        true,
        props,
        viewPatientOrigin,
        scope
      ).catch((error) => {
        if (isNotReadyReportError(error)) return;
        throw error;
      });
    }
    requireCurrentPatientSync(scope);
    await props.getPatientsList();
  }
  return updatedPatientWithImgDataAndRestData;
}

export const handleManualSyncPatient = async (patient, props, exportParam) => {
  if (!patient) return;
  const needsReportStatus =
    patient.leftEyeStatus === "" ||
    patient.leftEyeStatus === "Pending" ||
    patient.rightEyeStatus === "" ||
    patient.rightEyeStatus === "Pending";
  // A report cannot exist before its images are registered server-side; the
  // realtime owner's socket-driven resync fetches once they land.
  if (needsReportStatus && patientHasServerImages(patient)) {
    let disposition = "missing";
    try {
      const claim = props?.mlStatusRepository?.claimResyncTarget?.(
        patientUploadResyncTargetKey(patient.mrn),
        "manual"
      );
      disposition = claim?.disposition || "missing";
    } catch (error) {
      disposition = "missing";
    }
    if (disposition !== "claimed" && disposition !== "ambiguous") {
      // No subscribed owner owns this scope (or realtime is unavailable);
      // fall back to the pre-socket direct fetch.
      getReportStatus(patient, patient.restDetailOnServer, props).catch(
        reportReportStatusFailure
      );
    }
  }
  if (!patient.basicDetailOnServer) {
    if (patient.hp_id !== undefined) {
      let updatedData = {
        mrn: patient.mrn,
        fname: patient.registrationData.basicDetails.firstName,
        mname: patient.registrationData.basicDetails.middleName,
        lname: patient.registrationData.basicDetails.lastName,
        email: patient.registrationData.basicDetails.email,
        gender: patient.registrationData.basicDetails.gender,
        // dob: patient.registrationData.basicDetails.dob,
        age: patient.registrationData.basicDetails.age,
      };

      const response = await axios({
        method: "put",
        url: syncBaseUrl + "/patient/" + patient.mrn,
        data: updatedData,
        headers: {
          Accept: "*/*",
          authorization: getAuthTokenFromSessionStorage(),
        },
      });
      const data = {
        mrn: patient.mrn,
        updatedBasicDetailOnServer: true,
        basicDetailOnServer: true,
      };
      requirePatientWrite(await updatePatient(data));
      Object.assign(patient, data);
      await props.getPatientsList();
      if (!patient.imgOnServer && !patient.imgDataOnServer) {
        return imagaAndRestDataSync(patient, props);
      }
      if (patient.basicDetailOnServer && !patient.restDetailOnServer) {
        return restDataSync(patient, props);
      }
      return response;
    }
    ///////////////////basic detail reg start update index db and get new data
    else {
      const localMrn = patient.mrn;
      try {
        const res = await axios({
          method: "post",
          url: syncBaseUrl + "/patient",
          data: {
            mrn: patient.mrn,
            ...patient.registrationData,
            ...props.globalStore.userAgentData.ua__summary,
          },
          headers: {
            Accept: "*/*",
            authorization: getAuthTokenFromSessionStorage(),
          },
        });
        props.isRegisteredResponse({ message: "Sucessfully Added", error: false });
        const data = {
          ...patient,
          mrn: res.data.mrn,
          hp_id: res.data.hp_id,
          basicDetailOnServer: true,
        };
        props.setActivePatientRealtimeMrn?.(res.data.mrn);
        const realtimeProps = {
          ...props,
          patientStore: {
            ...props.patientStore,
            activeRealtimePatientMrn: res.data.mrn,
          },
        };
        await updatePatientID(res.data.mrn, data);
        Object.assign(patient, data);
        props.getPatientDataById(res.data.mrn);
        if (exportParam) {
          const { firstName: firstname, lastName: lastname, gender: sex, age } =
            data.registrationData.basicDetails;
          const { address, mobileNo: phone } =
            data.personalData.personalContactsDetails;
          const pid = data.mrn;
          const exportJson = {
            firstname,
            lastname,
            age,
            sex,
            address,
            phone,
            pid,
            _pid: pid,
            patientid: pid,
            creationdate: moment().format("YYYY-MM-DD hh:mm:ss:ms"),
          };
          const blob = new Blob([JSON.stringify(exportJson, null, 2)], {
            type: "application/json",
          });
          saveAs(blob, `${pid}_${firstname}_${lastname}`);
        }
        await deletePatient(localMrn);
        const patientRestData = stripInlineBase64FromWire({
          registrationData: data.registrationData,
          personalData: data.personalData,
          patientHistoryData: data.patientHistoryData,
          lifeStyleData: data.lifeStyleData,
          clinicalConditionData: data.clinicalConditionData,
        });
        try {
          await axios({
            method: "post",
            url: syncBaseUrl + "/patient-reg",
            data: {
              mrn: data.mrn,
              ...patientRestData,
              ...props.globalStore.userAgentData.ua__summary,
            },
            headers: {
              Accept: "*/*",
              authorization: getAuthTokenFromSessionStorage(),
              "Access-Control-Allow-Origin": "*",
            },
          });
        } catch (ignored) {
          // Preserve the existing image-sync fallback when the REST detail write fails.
        }
        if (data.basicDetailOnServer && !data.imgOnServer && !data.imgDataOnServer) {
          const result = await imagaAndRestDataSync(data, realtimeProps);
          await props.getPatientsList();
          await props.getPatientDataById?.(res.data.mrn);
          return result;
        }
        return { outcome: "applied", patient: data };
      } catch (error) {
          // advenio-ko7t — an "already exists" rejection means the server
          // already holds this patient under a different mrn. Adopt it
          // instead of surfacing a dead-end alert: swap the local row to the
          // server identity and continue the upload/rest chain, mirroring
          // the success path.
          const duplicateStatus = error?.response?.status;
          const duplicateMessage = String(
            error?.response?.data?.error || error?.response?.data?.message || ""
          );
          const isDuplicateRegistration =
            duplicateStatus === 409 ||
            (duplicateStatus === 503 &&
              duplicateMessage.toLowerCase().includes("already exists"));
          if (isDuplicateRegistration) {
            try {
              const lookup = await axios({
                method: "get",
                url: syncBaseUrl + "/patient/" + encodeURIComponent(patient.registrationData.basicDetails.email),
                headers: {
                  Accept: "*/*",
                  authorization: getAuthTokenFromSessionStorage(),
                },
              });
              const serverPatient = Array.isArray(lookup?.data)
                ? lookup.data[0]
                : null;
              if (serverPatient?.mrn && patient.mrn !== serverPatient.mrn) {
                // advenio-ko7t review hardening — the attempt was REJECTED,
                // so its form payload (fname/hp_id/age…) must never reach the
                // adopted survivor's local record: updatePatientID is a
                // wholesale Dexie put and would overwrite the survivor's
                // registrationData with the attempt's identity fields (live
                // UI corruption caught by browser-qa). The put drops the
                // attempt's registrationData entirely — the survivor's own
                // server truth stays authoritative; basicDetailOnServer=true
                // guarantees this branch (or the error purge below) is the
                // only writer. Image/retinal data still follows the swap.
                const data = {
                  ...patient,
                  mrn: serverPatient.mrn,
                  hp_id: serverPatient.hp_id,
                  basicDetailOnServer: true,
                  registrationData: undefined,
                };
                props.isRegisteredResponse({
                  message: "Patient already registered. Continuing upload.",
                  error: false,
                });
                props.setActivePatientRealtimeMrn?.(serverPatient.mrn);
                await updatePatientID(serverPatient.mrn, data);
                Object.assign(patient, data);
                // The swapped in-memory object no longer carries the rejected
                // attempt's form data; keep Dexie in step for the upload
                // chain's persistPatientUploadState writes below.
                patient.registrationData = undefined;
                await deletePatient(localMrn);
                const realtimeProps = {
                  ...props,
                  patientStore: {
                    ...props.patientStore,
                    activeRealtimePatientMrn: serverPatient.mrn,
                  },
                };
                const result = await imagaAndRestDataSync(data, realtimeProps);
                await props.getPatientsList();
                await props.getPatientDataById?.(serverPatient.mrn);
                return result;
              }
            } catch (adoptError) {
              console.log("duplicate registration adopt failed", adoptError);
            }
          }
          console.log("basic detail error", error);

          // Handle all errors, not just 409
          let message;
          if (error?.response?.status === 409) {
            message = "Email Id already exists";
          } else {
            // Extract error message from response or use generic fallback
            message = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Registration failed. Please try again.";
          }

          props.isRegisteredResponse({
            message: message,
            error: true,
          });

          // Always restore Register buttons so user can retry
          props.saceOrUpdateBtn(true);
          // A successful adopt above swaps patient.mrn off localMrn, so this
          // purge only ever removes the un-adopted ghost row.
          if (patient.mrn === localMrn) {
            await deletePatient(localMrn);
            await props.getPatientsList();
          }
          return { outcome: "error", error };
      }
    }
  } else if (!patient.imgOnServer && !patient.imgDataOnServer) {
    return imagaAndRestDataSync(patient, props);
  } else if (patient.basicDetailOnServer && !patient.restDetailOnServer) {
    return restDataSync(patient, props);
  }
  return { outcome: "ignored" };
};
